import { PageRequest } from '@/core/request/PageRequest';
import { useServiceError } from '@/hooks';
import { notification } from 'antd';
import { useIntl } from 'react-intl';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import {
  calculateTaxesItemPurchase,
  getPurchaseItem,
  getPurchaseItemsTags,
  itemPurchaseRemove,
  itemPurchaseReturnGenerateInvoice,
  listItemPurchasesReturn,
  purchaseItemCreate,
  purchaseItemListCreate,
  purchaseItemUpdate,
  purchasesItemsList,
  purchasesReturnAllItemsList,
  purchaseItemsSellPriceUpdate,
  purchaseItemsUpdateByPartId,
  purchaseItemsWorkOrderLink,
  purchaseItemsRemoveWorkOrderLink,
  purchaseItemsAddWorkOrderItems,
  purchaseItemCreatePart,
} from './PurchaseItem.service';
import {
  CreatePurchaseItemRequest,
  ItemsPurchaseRequest,
  ItemsPurchaseReturnAllRequest,
  PurchaseRemoveRequest,
  ReturnItemQuery,
  UpdatePurchaseItemRequest,
} from './PurchaseItem.types';
import notificationError from '@/utils/functions/notificationError';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { purchaseItemDicionary } from './PurchaseItem.errors';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';

const QUERY_ID = 'items-purchase';

export function useItemsPurchases(params?: ItemsPurchaseRequest) {
  const query = useInfiniteQuery(
    [QUERY_ID, { ...params }],
    ({ pageParam }) => {
      const queryParams: PageRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };
      return purchasesItemsList(queryParams);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
      retry: 1,
      enabled: params?.enabled,
    },
  );

  useServiceError(query.error);

  return query;
}

export function usePurchaseItemCreate() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (obj: CreatePurchaseItemRequest) => purchaseItemCreate(obj),
    {
      onSuccess() {
        queryClient.invalidateQueries(['purchase/detail']);
        notification.success({
          message: intl.formatMessage({
            id: 'itemsPurchase.notifications.create.success.title',
          }),
          description: intl.formatMessage({
            id: 'itemsPurchase.notifications.create.success.description',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function usePurchaseListItemCreate() {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    (obj: CreatePurchaseItemRequest[]) => purchaseItemListCreate(obj),
    {
      onSuccess(_, variables) {
        notification.success({
          message: intl.formatMessage({
            id: 'itemsPurchase.notifications.create.success.title',
          }),
          description: intl.formatMessage({
            id: 'itemsPurchase.notifications.create.success.description',
          }),
        });

        queryChanel({
          invalidateQueries: [
            [QUERY_ID],
            ['item/detail', variables.at(0)?.purchase],
            ['purchase/detail', variables.at(0)?.purchase],
          ],
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function usePurchaseItemUpdate() {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    (obj: UpdatePurchaseItemRequest & { noNotification?: boolean }) =>
      purchaseItemUpdate(obj),
    {
      async onSuccess(_, data) {
        !data.noNotification &&
          notification.success({
            message: intl.formatMessage({
              id: 'itemsPurchase.notifications.update.success.title',
            }),
            description: intl.formatMessage({
              id: 'itemsPurchase.notifications.update.success.description',
            }),
          });

        queryChanel({
          invalidateQueries: [
            ['purchase/detail'],
            ['item/detail', data.id],
            ['purchase/detail', data.purchase],
          ],
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useItemPurchaseRemove() {
  const queryClient = useQueryClient();
  const intl = useIntl();

  const mutation = useMutation(
    (obj: PurchaseRemoveRequest) => itemPurchaseRemove(obj),
    {
      async onSuccess(_, data) {
        await queryClient.invalidateQueries([`${QUERY_ID}-return`]);
        await queryClient.invalidateQueries([QUERY_ID]);
        await queryClient.invalidateQueries([
          'purchase/detail',
          data.purchaseId,
        ]);

        notification.success({
          message: intl.formatMessage({
            id: 'purchases.details.notifications.remove.success.title',
          }),
          description: intl.formatMessage({
            id: 'purchases.details.notifications.remove.success.description',
          }),
        });
      },

      onError(error) {
        notificationError(error, {
          translate(errors) {
            return errors.map((err) => purchaseItemDicionary[err] || err);
          },
        });
      },
    },
  );

  return mutation;
}

export function usePurchaseItem(itemId: number) {
  const query = useQuery(['item/detail', itemId], () =>
    getPurchaseItem(itemId),
  );

  useServiceError(query.error);

  return query;
}

export function useCalcTaxesItemPurchases() {
  const queryChanel = useQueryChanel();
  const intl = useIntl();

  const mutation = useMutation(
    (obj: UpdatePurchaseItemRequest) => {
      return calculateTaxesItemPurchase(obj);
    },
    {
      async onSuccess(_, variables) {
        queryChanel({
          invalidateQueries: [
            ['purchase/detail'],
            ['item/detail'],
            ['item/detail', variables.id],
          ],
        });

        notification.success({
          message: intl.formatMessage({
            id: 'itemsPurchase.notifications.update.success.title',
          }),
          description: intl.formatMessage({
            id: 'itemsPurchase.notifications.calcTaxes.success.description',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useItemsPurchasesReturn(
  params?: ReturnItemQuery,
  enabled?: boolean,
  showNotification = true,
) {
  const query = useInfiniteQuery(
    [`${QUERY_ID}-return`, { ...params }],
    ({ pageParam }) => {
      const queryParams: ReturnItemQuery = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };
      return listItemPurchasesReturn(queryParams);
    },
    {
      enabled: enabled || !!(params?.companyId && params?.supplierId),
      getNextPageParam: (lastPage) => lastPage.nextToken,

      onSuccess(data) {
        const itemsPurchaseReturn = flatInfiniteData(data);

        if (showNotification && !itemsPurchaseReturn.length) {
          notification.warning({
            message: 'Não foram encontrados itens para realizar devolução',
          });
        }
      },
    },
  );

  useServiceError(query.error);

  return query;
}

export function useItemsPurchaseReturnGenerateInvoice() {
  const queryClient = useQueryClient();

  const mutation = useMutation(itemPurchaseReturnGenerateInvoice, {
    onSuccess() {
      notification.success({
        message: 'Sucesso!',
        description: 'Devolução feita com sucesso!',
      });

      queryClient.invalidateQueries('items-purchase-return');
    },

    onError(error) {
      notificationError(error, {
        translate(errors) {
          return errors.map((err) =>
            err.includes('stock.errors.stock_not_available')
              ? 'Algum item selecionado está com estoque menor que a quantidade a devolver'
              : purchaseItemDicionary[err] || err,
          );
        },
      });
    },
  });

  return mutation;
}

export function useItemsPurchasesReturnAll(
  params?: ItemsPurchaseReturnAllRequest,
) {
  const query = useInfiniteQuery(
    [QUERY_ID, { ...params }],
    ({ pageParam }) => {
      const queryParams: PageRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };
      return purchasesReturnAllItemsList(queryParams);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
      retry: 1,
    },
  );

  useServiceError(query.error);

  return query;
}

export function usePurchaseItemsTags(purchaseId: number, enabled = true) {
  const query = useQuery(
    ['items/tags', purchaseId],
    () => getPurchaseItemsTags(purchaseId),
    { enabled },
  );

  useServiceError(query.error);

  return query;
}

export function usePurchaseItemsSellPriceUpdate() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationFn: purchaseItemsSellPriceUpdate,
    onSuccess() {
      notification.success({
        message: 'Sucesso!',
        description: 'Preço de Venda alterado!',
      });

      queryChanel({ invalidateQueries: [['items-purchase']] });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function usePurchaseItemsUpdateByPartId() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationFn: purchaseItemsUpdateByPartId,
    onSuccess() {
      notification.success({
        message: 'Sucesso!',
        description: 'Peça atualizado com sucesso!',
      });

      queryChanel({ invalidateQueries: [['items-purchase']] });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function usePurchaseItemsWorkOrderLink() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationFn: purchaseItemsWorkOrderLink,
    onSuccess() {
      notification.success({
        message: 'Sucesso!',
        description: 'Peça vinculada a Ordem de Serviço!',
      });

      queryChanel({ invalidateQueries: [['items-purchase']] });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function usePurchaseItemsRemoveWorkOrderLink() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationFn: purchaseItemsRemoveWorkOrderLink,
    onSuccess() {
      notification.success({
        message: 'Sucesso!',
        description: 'Peça desvinculada da Ordem de Serviço!',
      });

      queryChanel({ invalidateQueries: [['items-purchase']] });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function usePurchaseItemsAddWorkOrderItems() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationFn: purchaseItemsAddWorkOrderItems,
    onSuccess() {
      notification.success({
        message: 'Sucesso!',
        description: 'Peça cadastrada na Ordem de Serviço!',
      });

      queryChanel({ invalidateQueries: [['items-purchase']] });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function usePurchaseItemCreatePart() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationFn: purchaseItemCreatePart,
    onSuccess() {
      notification.success({
        message: 'Sucesso!',
        description: 'Peça cadastrada com sucesso!',
      });

      queryChanel({ invalidateQueries: [['items-purchase']] });
    },
    onError(error) {
      notificationError(error);
    },
  });
}
